import { Navigate, Route, Routes } from 'react-router-dom';

import BarangayList from './Barangays/BarangayList';
import BarangayStats from './ThrowingStats/BarangayStats';
import BarangayVoters from './Barangays/BarangayVoters';
import BuildDapaDashboard from './Utilities/BuildDapaDashboard';
import CandidateStats from './Dashboard/CandidateStats';
import ContactAdmin from './ContactAdmin';
import DapaAdditionalCSVImport from './Utilities/DapaAdditionalCSVImport';
import DapaCSVImport from './Utilities/DapaCSVImport';
import DapaImportErrorList from './Utilities/DapaImportErrorList';
import Dashboard from './Dashboard/Dashboard';
import DelCarmenImport from './Utilities/DelCarmenImport';
import DistrictStats from './ThrowingStats/DistrictStats';
import ExitPollsDashboard from './ExitPolls/ExitPollsDashboard';
import FinalListReport from './Reports/FinalList/FinalListReport';
import LeadersReport from './Reports/Leaders/LeadersReport';
import MunicipalStats from './ThrowingStats/MunicipalStats';
import OrganizationForm from './Organizations/OrganizationForm';
import OrganizationList from './Organizations/OrganizationList';
import PilarBatchPoll from './Utilities/PilarBatchPoll';
import PollForm from './Polls/PollForm';
import PollQuestionnaire from './Polls/PollQuestionnaire';
import PollResponseReport from './Reports/Poll/PollResponseReport';
import PollResponses from './Polls/PollResponses';
import PollsList from './Polls/PollsList';
import PrecinctDetails from './Precincts/PrecinctDetails';
import PrecinctList from './Precincts/PrecinctList';
import PrecinctVotersReport from './Reports/PrecinctVotersReport';
import PurgeVoters from './Utilities/PurgeVoters';
import React from 'react';
import ReindexVoterKeywords from './Utilities/ReindexVoterKeywords';
import RemoveTabMismatch from './Utilities/RemoveTabMismatch';
import ReplicateVote from './Utilities/ReplicateVote';
import ResolvePrecintVoters from './Utilities/ResolvePrecintVoters';
import ScanPurposePage from './Voters/ScanPurposePage';
import ScanVoterQR from './Voters/ScanVoterQR';
import SocorroCSVImport from './Utilities/SocorroCSVImport';
import SocorroImportErrorList from './Utilities/SocorroImportErrorList';
import SurveyReport from './Reports/SurveyReport';
import TVMonitor from './Dashboard/TVMonitor';
import UpdateVoterCount from './Utilities/UpdateVoterCount';
import UpmergePollResponses from './Utilities/UpmergePollResponses';
import UserManagement from './UserManagement';
import Utilities from './Utilities/Utilities';
import VoterCSVImport from './Utilities/VoterCSVImport';
import VoterDetails from './Voters/VoterDetails';
import VoterForm from './Voters/VoterForm';
import VoterGroupForm from './VoterGroups/VoterGroupForm';
import VoterGroupList from './VoterGroups/VoterGroupList';
import VoterGroupsReport from './Reports/VoterGroups/VoterGroupsReport';
import VoterList from './Voters/VoterList';
import VoterPreferenceReport from './Reports/VoterPreference/VoterPreferenceReport';
import { useAuth } from '../hooks/useAuth';

const AppRoutes: React.FC = () => {
  const { user, loading, hasPermissions } = useAuth();
  const isAdmin = user?.permissions?.admin || false;

  if (loading) {
    return null;
  }

  return (
    <Routes>
      <Route path="/" element={user ? (hasPermissions ? <Navigate to="/dashboard" /> : <ContactAdmin />) : <Navigate to="/login" />} />
      
      <Route path="/dashboard" element={user && hasPermissions && user.permissions.view_dashboard ? <Dashboard /> : <Navigate to="/polls" />} />
      <Route path="/dashboard/candidateStats" element={user && hasPermissions ? <CandidateStats /> : <Navigate to="/polls" />} />

      <Route path="/throwing" element={user && hasPermissions && user.permissions.view_throwing ? <DistrictStats /> : <Navigate to="/" />} />
      <Route path="/throwing/municipal/:municipalId" element={user && hasPermissions && user.permissions.view_throwing ? <MunicipalStats /> : <Navigate to="/" />} />
      <Route path="/throwing/barangay/:barangayId" element={user && hasPermissions && user.permissions.view_throwing ? <BarangayStats /> : <Navigate to="/" />} />
      
      <Route path="/tv" element={<TVMonitor />} />

      <Route path="/polls" element={user && hasPermissions ? <PollsList /> : <Navigate to="/" />} />
      <Route path="/polls/new" element={user && hasPermissions ? <PollForm /> : <Navigate to="/" />} />
      <Route path="/polls/edit/:id" element={user && hasPermissions ? <PollForm /> : <Navigate to="/" />} />
      <Route path="/polls/conduct/:id" element={user && hasPermissions ? <PollQuestionnaire /> : <Navigate to="/" />} />
      
      <Route path="/exit-polls" element={user && hasPermissions ? <ExitPollsDashboard /> : <Navigate to="/" />} />

      <Route path="/responses" element={user && hasPermissions && (user.permissions.view_responses || user.permissions.conduct_polls) ? <PollResponses /> : <Navigate to="/" />} />

      <Route path="/users" element={user && (isAdmin || user.permissions.manage_users) ? <UserManagement /> : <Navigate to="/" />} />

      <Route path="/login" element={user ? <Navigate to="/" /> : <div>Please sign in to access the app</div>} />

      <Route path="/voters" element={user && hasPermissions && (user.permissions.view_voters || user.permissions.validate_polls) ? <VoterList/> : <Navigate to="/" />}/>
      <Route path="/voters/new" element={user && hasPermissions && user.permissions.view_voters ? <VoterForm/> : <Navigate to="/" />}/>
      <Route path="/voters/edit/:id" element={user && hasPermissions && user.permissions.view_voters ? <VoterForm /> : <Navigate to="/" />} />
      <Route path="/voters/details/:voterId" element={user && hasPermissions ? <VoterDetails /> : <Navigate to="/" />} />
      
      <Route path="/scan-purpose" element={user && hasPermissions && user.permissions.view_voters ? <ScanPurposePage /> : <Navigate to="/" />} />
      <Route path="/scan-voter-qr" element={user && hasPermissions && user.permissions.view_voters ? <ScanVoterQR /> : <Navigate to="/" />} />

      <Route path="/voter-groups" element={user && hasPermissions && user.permissions.view_voter_groups ? <VoterGroupList/> : <Navigate to="/" />}/>
      <Route path="/voter-groups/new" element={user && hasPermissions && user.permissions.view_voter_groups ? <VoterGroupForm/> : <Navigate to="/" />}/>
      <Route path="/voter-groups/edit/:groupId" element={user && hasPermissions && user.permissions.view_voter_groups ? <VoterGroupForm /> : <Navigate to="/" />} />

      <Route path="/organizations" element={user && hasPermissions && user.permissions.manage_organizations ? <OrganizationList/> : <Navigate to="/" />}/>
      <Route path="/organizations/new" element={user && hasPermissions && user.permissions.manage_organizations ? <OrganizationForm/> : <Navigate to="/" />}/>
      <Route path="/organizations/edit/:id" element={user && hasPermissions && user.permissions.manage_organizations ? <OrganizationForm /> : <Navigate to="/" />} />

      <Route path="/precincts" element={user && hasPermissions && user.permissions.view_precincts ? <PrecinctList /> : <Navigate to="/" />} />
      <Route path="/precincts/:barangayId/:precinctNumber" element={user && hasPermissions && user.permissions.view_precincts ? <PrecinctDetails /> : <Navigate to="/" />} />

      <Route path="/barangays" element={user && hasPermissions && user.permissions.view_barangays ? <BarangayList /> : <Navigate to="/" />} />
      <Route path="/barangay-voters" element={user && hasPermissions && user.permissions.view_barangays ? <BarangayVoters /> : <Navigate to="/" />} />

      <Route path="/reports/survey" element={user && hasPermissions && user.permissions.view_reports ? <SurveyReport /> : <Navigate to="/" />} />
      <Route path="/reports/precinct-voters" element={user && hasPermissions && user.permissions.view_reports ? <PrecinctVotersReport /> : <Navigate to="/" />} />
      <Route path="/reports/poll/responses" element={user && hasPermissions && user.permissions.view_reports ? <PollResponseReport /> : <Navigate to="/" />} />
      <Route path="/reports/voters/preference" element={user && hasPermissions && user.permissions.view_reports ? <VoterPreferenceReport /> : <Navigate to="/" />} />
      <Route path="/reports/voter-groups" element={user && hasPermissions && user.permissions.view_reports && user.permissions.view_voter_groups ? <VoterGroupsReport /> : <Navigate to="/" />} />
      <Route path="/reports/leaders" element={user && hasPermissions && user.permissions.view_reports && user.permissions.view_voter_groups ? <LeadersReport /> : <Navigate to="/" />} />
      <Route path="/reports/final-list" element={user && hasPermissions && user.permissions.view_reports ? <FinalListReport /> : <Navigate to="/" />} />
      
      <Route path="/utilities" element={user && isAdmin ? <Utilities /> : <Navigate to="/" />} />
      <Route path="/utilities/voters/import/csv" element={user && isAdmin ? <VoterCSVImport /> : <Navigate to="/" />} />
      <Route path="/utilities/del-carmen/import/csv" element={user && isAdmin ? <DelCarmenImport /> : <Navigate to="/" />} />
      <Route path="/utilities/dapa/import/csv" element={user && isAdmin ? <DapaCSVImport /> : <Navigate to="/" />} />
      <Route path="/utilities/dapa/additional-import/csv" element={user && isAdmin ? <DapaAdditionalCSVImport /> : <Navigate to="/" />} />
      <Route path="/utilities/dapa/import-errors" element={user && isAdmin ? <DapaImportErrorList /> : <Navigate to="/" />} />
      <Route path="/utilities/socorro/import/csv" element={user && isAdmin ? <SocorroCSVImport /> : <Navigate to="/" />} />
      <Route path="/utilities/socorro/import-errors" element={user && isAdmin ? <SocorroImportErrorList /> : <Navigate to="/" />} />
      <Route path="/utilities/voters/keywords-index/refresh" element={user && isAdmin ? <ReindexVoterKeywords /> : <Navigate to="/" />} />
      <Route path="/utilities/precinct/resolve-voters" element={user && isAdmin ? <ResolvePrecintVoters /> : <Navigate to="/" />} />
      <Route path="/utilities/update-voter-count" element={user && isAdmin ? <UpdateVoterCount /> : <Navigate to="/" />} />
      <Route path="/utilities/remove-tab-mismatch" element={user && isAdmin ? <RemoveTabMismatch /> : <Navigate to="/" />} />
      <Route path="/utilities/replicate-vote" element={user && isAdmin ? <ReplicateVote /> : <Navigate to="/" />} />
      <Route path="/utilities/build_dapa_dashboard" element={user && isAdmin ? <BuildDapaDashboard /> : <Navigate to="/" />} />
      <Route path="/utilities/upmerge_poll_responses" element={user && isAdmin ? <UpmergePollResponses /> : <Navigate to="/" />} />
      <Route path="/utilities/purge-voters" element={user && isAdmin ? <PurgeVoters /> : <Navigate to="/" />} />
      <Route path="/utilities/pilar-batch-poll" element={user && isAdmin ? <PilarBatchPoll /> : <Navigate to="/" />} />
    </Routes>
  );
};

export default AppRoutes;
